/**
 * Global AudioContext Manager
 *
 * Manages a singleton AudioContext shared across all audio players to avoid
 * hitting browser limits (typically 6-8 AudioContext instances per origin).
 *
 * Uses reference counting to properly cleanup when no players are active.
 */

class AudioContextManager {
  private static instance: AudioContext | null = null;
  private static refCount: number = 0;
  private static cleanupTimer: NodeJS.Timeout | null = null;
  private static readonly CLEANUP_DELAY = 30000; // 30 seconds

  /**
   * Acquire a reference to the shared AudioContext.
   * Creates a new context if none exists or if the previous one was closed.
   *
   * @returns The shared AudioContext instance
   */
  static acquire(): AudioContext {
    // Cancel any pending cleanup
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    // Create new context if needed
    if (!this.instance || this.instance.state === 'closed') {
      this.instance = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('🎵 AudioContextManager: Created new shared AudioContext');
    }

    this.refCount++;
    console.log(`🎵 AudioContextManager: Acquired context (refCount: ${this.refCount})`);

    return this.instance;
  }

  /**
   * Release a reference to the shared AudioContext.
   * Schedules cleanup after a delay if no more references exist.
   */
  static release(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    console.log(`🎵 AudioContextManager: Released context (refCount: ${this.refCount})`);

    // Schedule cleanup if no more users
    if (this.refCount <= 0 && this.instance) {
      // Wait before closing to allow for quick re-acquisition
      this.cleanupTimer = setTimeout(() => {
        if (this.refCount <= 0 && this.instance) {
          console.log('🎵 AudioContextManager: Closing unused AudioContext');
          this.instance.close().catch(err =>
            console.warn('AudioContextManager: Error closing context:', err)
          );
          this.instance = null;
          this.cleanupTimer = null;
        }
      }, this.CLEANUP_DELAY);
    }
  }

  /**
   * Get current reference count (for debugging/monitoring)
   */
  static getRefCount(): number {
    return this.refCount;
  }

  /**
   * Check if a shared context exists and is active
   */
  static isActive(): boolean {
    return this.instance !== null && this.instance.state !== 'closed';
  }

  /**
   * Force immediate cleanup (useful for testing or emergency scenarios)
   */
  static forceCleanup(): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    if (this.instance) {
      console.log('🎵 AudioContextManager: Force closing AudioContext');
      this.instance.close().catch(err =>
        console.warn('AudioContextManager: Error during force cleanup:', err)
      );
      this.instance = null;
    }

    this.refCount = 0;
  }
}

export default AudioContextManager;
